import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';
import { toast } from 'vue-sonner';
import { isImageUploadedFile } from '~/lib/uploadedFiles';
import { downloadUploadedFilesAsZip } from '~/lib/zipArchive';
import type { UploadedFile } from '~/types/models';

interface UploadedFileActionOptions {
    canMutate: MaybeRefOrGetter<boolean>;
    onChanged?: () => void | Promise<void>;
}

export function useUploadedFileActions(options: UploadedFileActionOptions) {
    const filesStore = useFilesStore();
    const tenantStore = useTenantStore();
    const busyFileId = ref('');
    const actionError = ref('');
    const deleteCandidate = ref<UploadedFile | null>(null);
    const editCandidate = ref<UploadedFile | null>(null);
    const bulkDeleteCandidates = ref<UploadedFile[]>([]);
    const bulkLoading = ref(false);
    const bulkLoadingLabel = ref('Updating files…');
    const deleteOpen = computed({
        get: () => Boolean(deleteCandidate.value),
        set: (open: boolean) => {
            if (!open) deleteCandidate.value = null;
        },
    });
    const editOpen = computed({
        get: () => Boolean(editCandidate.value),
        set: (open: boolean) => {
            if (!open) editCandidate.value = null;
        },
    });
    const bulkDeleteOpen = computed({
        get: () => bulkDeleteCandidates.value.length > 0,
        set: (open: boolean) => {
            if (!open) bulkDeleteCandidates.value = [];
        },
    });

    function requireMutation() {
        if (!toValue(options.canMutate)) {
            throw new Error('You do not have permission to change files.');
        }
    }

    async function afterChange() {
        await options.onChanged?.();
    }

    function requestDelete(file: UploadedFile) {
        if (!toValue(options.canMutate)) return;
        deleteCandidate.value = file;
    }

    function requestEdit(file: UploadedFile) {
        if (!toValue(options.canMutate) || !isImageUploadedFile(file)) return;
        actionError.value = '';
        editCandidate.value = file;
    }

    function requestBulkDelete(files: UploadedFile[]) {
        if (!toValue(options.canMutate)) return;
        bulkDeleteCandidates.value = [...new Map(files.map((file) => [file.id, file])).values()];
    }

    async function confirmBulkDelete(): Promise<boolean> {
        const files = bulkDeleteCandidates.value;
        if (!files.length) return false;

        bulkLoading.value = true;
        bulkLoadingLabel.value = 'Deleting files…';
        actionError.value = '';
        try {
            requireMutation();
            const selectedIds = new Set(files.map((file) => file.id));
            const coverReplacements = new Map<string, UploadedFile | null>();
            for (const file of files) {
                if (file.entityType !== 'project' || !file.isProjectCover || coverReplacements.has(file.entityId)) continue;
                const remaining = (filesStore.filesByEntity[`project:${file.entityId}`] || [])
                    .filter((entry) => !selectedIds.has(entry.id) && isImageUploadedFile(entry));
                coverReplacements.set(file.entityId, remaining[0] || null);
            }

            for (const file of files) await filesStore.deleteFile(file.entityType, file.entityId, file);
            for (const [projectId, replacement] of coverReplacements) {
                if (replacement?.id) await filesStore.setProjectCover(projectId, replacement.id);
                else await filesStore.clearProjectCover(projectId);
            }

            bulkDeleteCandidates.value = [];
            await afterChange();
            toast.success(`${files.length} file${files.length === 1 ? '' : 's'} deleted.`);
            return true;
        } catch (error: any) {
            actionError.value = error?.message || 'Could not delete the selected files.';
            await afterChange();
            toast.error(actionError.value);
            return false;
        } finally {
            bulkLoading.value = false;
        }
    }

    async function assignFilesToProject(files: UploadedFile[], projectId: string): Promise<boolean> {
        if (!files.length) return false;
        bulkLoading.value = true;
        bulkLoadingLabel.value = 'Assigning files…';
        actionError.value = '';
        try {
            requireMutation();
            if (files.some((file) => file.entityType !== 'client')) {
                throw new Error('Only files held at client level can be assigned to a project.');
            }
            for (const file of files) await filesStore.assignClientFileToProject(file, projectId);
            await afterChange();
            toast.success(`${files.length} file${files.length === 1 ? '' : 's'} assigned to the project.`);
            return true;
        } catch (error: any) {
            actionError.value = error?.message || 'Could not assign the selected files.';
            toast.error(actionError.value);
            return false;
        } finally {
            bulkLoading.value = false;
        }
    }

    async function downloadFilesAsZip(files: UploadedFile[], archiveName?: string): Promise<boolean> {
        if (!files.length) return false;
        bulkLoading.value = true;
        bulkLoadingLabel.value = 'Preparing ZIP…';
        actionError.value = '';
        try {
            const tenantId = tenantStore.currentTenantId;
            const { $auth } = useNuxtApp();
            const token = await $auth.currentUser?.getIdToken();
            if (!tenantId || !token) throw new Error('You need to be signed in to download these files.');

            const downloaded = await downloadUploadedFilesAsZip(files, archiveName || 'uploads.zip', (progress) => {
                bulkLoadingLabel.value = progress.phase === 'finalising'
                    ? 'Finalising ZIP…'
                    : `Downloading ${Math.min(progress.completed + 1, progress.total)} of ${progress.total}…`;
            }, file => fetch(`/api/files/${encodeURIComponent(file.id)}/download?tenantId=${encodeURIComponent(tenantId)}`, {
                headers: { authorization: `Bearer ${token}` },
            }));
            if (!downloaded) return false;
            toast.success('ZIP download ready.');
            return true;
        } catch (error: any) {
            actionError.value = error?.message || 'Could not create the ZIP download.';
            toast.error(actionError.value);
            return false;
        } finally {
            bulkLoading.value = false;
        }
    }

    async function saveImageDetails(payload: {
        file: UploadedFile;
        caption: string;
        altText: string;
        visibleInPortal: boolean;
    }) {
        busyFileId.value = payload.file.id;
        actionError.value = '';
        try {
            requireMutation();
            if (!isImageUploadedFile(payload.file)) throw new Error('Only image details can be edited here.');
            await filesStore.updateFileMetadata(payload.file.entityType, payload.file.entityId, payload.file.id, {
                galleryCaption: payload.caption,
                galleryAltText: payload.altText,
                ...(payload.file.entityType === 'project' ? { galleryVisibleInPortal: payload.visibleInPortal } : {}),
            });
            editCandidate.value = null;
            await afterChange();
            toast.success('Image details updated.');
        } catch (error: any) {
            actionError.value = error?.message || 'Could not update the image details.';
            toast.error(actionError.value);
        } finally {
            busyFileId.value = '';
        }
    }

    async function confirmDelete() {
        const file = deleteCandidate.value;
        if (!file) return;

        busyFileId.value = file.id;
        actionError.value = '';
        try {
            requireMutation();
            const entityKey = `${file.entityType}:${file.entityId}`;
            const remainingProjectImages = file.entityType === 'project'
                ? (filesStore.filesByEntity[entityKey] || []).filter((entry) => entry.id !== file.id && isImageUploadedFile(entry))
                : [];

            await filesStore.deleteFile(file.entityType, file.entityId, file);
            if (file.entityType === 'project' && file.isProjectCover) {
                const nextCover = remainingProjectImages[0];
                if (nextCover?.id) await filesStore.setProjectCover(file.entityId, nextCover.id);
                else await filesStore.clearProjectCover(file.entityId);
            }
            deleteCandidate.value = null;
            await afterChange();
            toast.success('File deleted.');
        } catch (error: any) {
            actionError.value = error?.message || 'Could not delete the file.';
            toast.error(actionError.value);
        } finally {
            busyFileId.value = '';
        }
    }

    async function setCover(file: UploadedFile) {
        busyFileId.value = file.id;
        actionError.value = '';
        try {
            requireMutation();
            if (file.entityType !== 'project' || !isImageUploadedFile(file)) throw new Error('Only project images can be used as a cover.');
            await filesStore.setProjectCover(file.entityId, file.id);
            await afterChange();
            toast.success('Project cover updated.');
        } catch (error: any) {
            actionError.value = error?.message || 'Could not update the project cover.';
            toast.error(actionError.value);
        } finally {
            busyFileId.value = '';
        }
    }

    async function togglePortalVisibility(file: UploadedFile) {
        busyFileId.value = file.id;
        actionError.value = '';
        try {
            requireMutation();
            if (file.entityType !== 'project') throw new Error('Only project files can be shared in the project portal.');
            await filesStore.updateFileMetadata('project', file.entityId, file.id, {
                galleryVisibleInPortal: file.galleryVisibleInPortal === false,
            });
            await afterChange();
            toast.success(file.galleryVisibleInPortal === false ? 'File is now visible in the client portal.' : 'File hidden from the client portal.');
        } catch (error: any) {
            actionError.value = error?.message || 'Could not update portal visibility.';
            toast.error(actionError.value);
        } finally {
            busyFileId.value = '';
        }
    }

    async function addToProject(payload: { file: UploadedFile, projectId: string }) {
        busyFileId.value = payload.file.id;
        actionError.value = '';
        try {
            requireMutation();
            await filesStore.assignClientFileToProject(payload.file, payload.projectId);
            await afterChange();
            toast.success('File added to project.');
        } catch (error: any) {
            actionError.value = error?.message || 'Could not add the file to that project.';
            toast.error(actionError.value);
        } finally {
            busyFileId.value = '';
        }
    }

    return {
        busyFileId,
        actionError,
        deleteCandidate,
        deleteOpen,
        editCandidate,
        editOpen,
        bulkDeleteCandidates,
        bulkDeleteOpen,
        bulkLoading,
        bulkLoadingLabel,
        requestDelete,
        requestEdit,
        requestBulkDelete,
        confirmDelete,
        confirmBulkDelete,
        saveImageDetails,
        setCover,
        togglePortalVisibility,
        addToProject,
        assignFilesToProject,
        downloadFilesAsZip,
    };
}
